import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { constants } from '../../constants';
import { Button } from '../../atoms';
import { CollapsibleContent } from './MessageCard.styles';
import { CollapsibleMessagesProps } from './MessageCard.types';

const { MAX_VIEWPORT_MOBILE } = constants;

export const CollapsibleMessages: FC<CollapsibleMessagesProps> = ({
  children,
  collapsible = false,
  moreLabel,
  lessLabel,
  maxCollapsedLines = 3,
}) => {
  const [isExpanded, setIsExpanded] = useState(!collapsible);
  const [shouldCollapse, setShouldCollapse] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const checkContentHeight = useCallback(() => {
    if (!contentRef.current || typeof window === 'undefined') return;
    if (window.innerWidth <= MAX_VIEWPORT_MOBILE) {
      const lineHeight = parseFloat(getComputedStyle(contentRef.current).lineHeight) || 21;
      const lines = contentRef.current.scrollHeight / lineHeight;
      setShouldCollapse(lines > maxCollapsedLines);
    } else {
      setShouldCollapse(false);
    }
  }, [maxCollapsedLines]);

  useEffect(() => {
    if (!collapsible) return;
    checkContentHeight();
    const handleResize = () => {
      checkContentHeight();
      if (window.innerWidth > MAX_VIEWPORT_MOBILE) {
        setIsExpanded(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [collapsible, checkContentHeight]);

  if (!collapsible) {
    return <>{children}</>;
  }

  return (
    <>
      <CollapsibleContent ref={contentRef} isExpanded={isExpanded} shouldCollapse={shouldCollapse}>
        {children}
      </CollapsibleContent>
      {collapsible && shouldCollapse && (
        <Button
          callback={() => setIsExpanded(!isExpanded)}
          linkAriaLabel={isExpanded ? lessLabel : moreLabel}
          type="button"
          className="button t-font-xs"
          text={isExpanded ? lessLabel : moreLabel}
        />
      )}
    </>
  );
};
